import React from "react";
import {
  Container,
  Stack,
  Box,
  Typography,
  Card,
  CardContent,
  Grid2,
  Divider,
  Chip,
  Alert,
  Icon,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import { useSelector } from "react-redux";
import {
  GlobalState,
  PaymentMethod,
  Rental,
} from "store/rentals-store";
import { formatDate } from "helpers/date-utils";
import Iframe from "components/atoms/iframe/iframe";
import { currencyFormat } from "helpers/currency-utils";

export default function Confirmation() {
  const rental: Rental = useSelector((state: GlobalState) => state.rental);
  const point = rental.delivery?.deliveryPoint;
  const deliveryAddress = point
    ? [point.address.streetName, point.address.apartmentNumber,
        point.address.neighborhood, point.address.addressLine2,
        point.address.town, point.address.region, point.address.zipCode,
        point.address.country].filter(Boolean).join(", ")
    : "";

  if (!rental.confirmation?.confirmationCode) {
    return null;
  }

  return (
    <Container sx={{ maxWidth: 900, mx: "auto", p: 3 }}>
      {/* Success Header */}
      <Stack spacing={2} alignItems="center" textAlign="center" mb={4}>
        <Icon fontSize="large" color="success">
          check_circle
        </Icon>
        <Typography variant="h4" fontWeight={600}>
          Reserva Confirmada!
        </Typography>
        <Typography color="text.secondary">Código de confirmación</Typography>
        <Chip
          label={rental.confirmation.confirmationCode}
          color="primary"
          sx={{ fontSize: 16, px: 2, py: 1 }}
        />
      </Stack>

      <Grid2 container spacing={3}>
        {/* Reservation Details */}
        <Grid2 size={{ xs: 12, md: 8 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Detalles de la reserva
              </Typography>

              <Divider sx={{ mb: 2 }} />

              <Stack spacing={2}>
                <Stack direction="row" spacing={1} alignItems="center">
                  {rental.totals.items.length > 1 ? (
                    <Box>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Icon color="action">directions_car</Icon>
                        <Typography>
                          {" "}
                          <strong>Artículos:</strong>
                        </Typography>
                      </Stack>
                      <List dense={true}>
                        {rental.totals.items.map((item) => (
                          <ListItem key={item.id}>
                            <ListItemText
                              primary={item.name + " - $" + item.price}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  ) : (
                    <Stack direction="row" spacing={1}>
                      <Icon color="action">directions_car</Icon>
                      <Typography>
                        <strong>Artículo:</strong>{" "}
                        {rental.totals.items.map((item) => item.name)}
                      </Typography>
                    </Stack>
                  )}
                </Stack>

                <Typography>
                  <strong>Fecha de inicio:</strong>{" "}
                  {formatDate(rental.startDate, "DD/MM/YYYY")}
                </Typography>

                <Typography>
                  <strong>Fecha de finalización:</strong>{" "}
                  {formatDate(rental.endDate, "DD/MM/YYYY")}
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid2>

        {/* Payment Summary */}
        <Grid2 size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Resumen de pago
              </Typography>

              <Divider sx={{ mb: 2 }} />

              <Stack spacing={2}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Icon color="action">payment</Icon>

                  <Typography>
                    <strong>Método: </strong>
                        {rental.payment.method === PaymentMethod.CASH && <span>Pago en efectivo</span>}
                        {rental.payment.method === PaymentMethod.TRANSFER && <span>Transferencia bancaria</span>}
                  </Typography>
                </Stack>

                {rental.payment.method === PaymentMethod.TRANSFER && rental.confirmation.bankDetails && (
                  <Stack>
                    <Typography component={"div"}>Datos de cuenta</Typography>
                    <List dense={true}>
                      <ListItem>
                        <ListItemText primary={`Titular de la cuenta: ${rental.confirmation.bankDetails.bankAccountHolder}`} />
                      </ListItem>
                      <ListItem>
                        <ListItemText primary={`Nombre del banco: ${rental.confirmation.bankDetails.bankName}`} />
                      </ListItem>
                      <ListItem>
                        <ListItemText primary={`Número de cuenta: ${rental.confirmation.bankDetails.bankAccountNumber}`} />
                      </ListItem>
                    </List>
                  </Stack>
                )}

                <Divider />

                <List disablePadding>
                  {rental.totals.items.map((item) => (
                    <React.Fragment key={`item-${item.id}`}>
                      <ListItem disableGutters sx={{ px: 0, py: 0.5 }}>
                        <ListItemText
                          primary={item.name}
                          secondary={`${currencyFormat(item.price)} / día`}
                        />
                        <Typography fontWeight={500}>
                          {currencyFormat(item.price * rental.totals.days)}
                        </Typography>
                      </ListItem>
                      {item.depositFee && (
                        <ListItem disableGutters sx={{ px: 0, py: 0 }}>
                          <ListItemText
                            primary={
                              <Typography variant="body2" color="text.secondary">
                                Depósito de seguridad <br /> (Reembolsable si no hay daños)
                              </Typography>
                            }
                          />
                          <Typography variant="body2" color="text.secondary" fontWeight={500}>
                            {currencyFormat(item.depositFee)}
                          </Typography>
                        </ListItem>
                      )}
                    </React.Fragment>
                  ))}
                </List>

                <Divider />

                <Typography variant="h5" color="primary" fontWeight={600}>
                  {currencyFormat(rental.totals.amountDue)}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  Total por {rental.totals.days} días de alquiler
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </Grid2>

        {/* Customer Info */}
        <Grid2 size={{ xs: 12 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Información del cliente
              </Typography>

              <Divider sx={{ mb: 2 }} />

              <Stack spacing={1}>
                <Typography>
                  <strong>Nombre:</strong> {rental.contactInfo.firstName}{" "}
                  {rental.contactInfo.lastName}
                </Typography>

                <Stack direction="row" spacing={1} alignItems="center">
                  <Icon color="action">email</Icon>
                  <Typography>
                    <strong>Email:</strong> {rental.contactInfo.email}
                  </Typography>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Grid2>

        <Grid2 size={{ xs: 12 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Información de entrega
              </Typography>

              <Divider sx={{ mb: 2 }} />

              <Stack direction="row" spacing={1} alignItems="center">
                <Icon color="action">local_shipping</Icon>
                <Typography>
                  <strong>Lugar de entrega: </strong>
                    {point?.name}
                </Typography>
              </Stack>
                <Stack direction="column" spacing={1} alignItems="center">
                  {point && (
                    <Box sx={{ width: "100%", mt: 2, overflowWrap: "anywhere" }}>
                      <Typography>{deliveryAddress}</Typography>
                      <Box sx={{ height: { xs: 260, md: 360 }, mt: 1 }}>
                        <Iframe src={`https://maps.google.com/maps?q=${encodeURIComponent(deliveryAddress)}&output=embed`} />
                      </Box>
                    </Box>
                  )}
                </Stack>
            </CardContent>
          </Card>
        </Grid2>

        {/* Next Steps */}
        <Grid2 size={{ xs: 12 }}>
          <Alert severity="info">
            Un correo electrónico de confirmación ha sido enviado a{" "}
            <strong>{rental.contactInfo.email}</strong>. Por favor, lleve un
            documento de identidad válido y licencia de conducir en el momento
            de la entrega.
          </Alert>
        </Grid2>
      </Grid2>
    </Container>
  );
}
